<script setup lang="ts">
import { computed, reactive, ref, useId } from 'vue'
import { storeToRefs } from 'pinia'
import Checkbox from 'primevue/checkbox'
import MarkdownText from '@/components/MarkdownText.vue'
import WikiText from '@/components/wikitext/WikiText.vue'
import PrintLayoutContainer from '@/print/PrintLayoutContainer.vue'
import PrintPage from '@/print/PrintPage.vue'
import PrintPreparing from '@/print/PrintPreparing.vue'
import PrintPreviewOverlay from '@/print/PrintPreviewOverlay.vue'
import { usePrintPreview } from '@/print/usePrintPreview'
import { useVisibilityStore } from '@/visibility/visibility.store'
import PrintableObjectiveIntro from './PrintableObjectiveIntro.vue'
import PrintSectionHeading from './PrintSectionHeading.vue'
import { useAssessmentItemsStore } from './assessment_items.store'
import { DEFAULT_BUTTON_CLASSES, levelButtonClasses, type ObjectiveLevel } from './objective_levels'
import { partHeading, shortCcssRef, type ObjectivePrintOptions, type PrintedObjectiveOptions } from './objective_print'

const FETCH_ERROR_MESSAGE = 'There was a problem preparing this item for printing. Please try again.'

const props = withDefaults(
  defineProps<{
    itemId: string
    objectiveLevel: ObjectiveLevel
    objectiveTitle?: string
    objectiveText?: string
  }>(),
  { objectiveTitle: '', objectiveText: '' }
)

const emit = defineEmits<{
  close: []
  print: [options: PrintedObjectiveOptions]
}>()

const { teksVisibility } = storeToRefs(useVisibilityStore())
const itemsStore = useAssessmentItemsStore()
const { isTransitioning, isTrial, printQuota, printQuotaExceeded, printsRemaining, printAndClose } = usePrintPreview(close)

const optionIdPrefix = useId()
const isFetchingItem = ref(true)
const errorMessage = ref('')
const printOptions = reactive<ObjectivePrintOptions>({
  includeObjective: true,
  includeItem: true,
  includePassages: true,
  includeResponse: true
})

const item = computed(() => itemsStore.itemById(props.itemId))
const hasPassages = computed(() => (item.value?.passages.length ?? 0) > 0)
const hasMultipleParts = computed(() => (item.value?.parts.length ?? 0) > 1)
const isPreparing = computed(() => isFetchingItem.value || isTransitioning.value)
const printButtonClasses = computed(() => levelButtonClasses(props.objectiveLevel))
const printQuotaClass = computed(() => (printQuotaExceeded.value ? 'text-red-300' : 'text-gray-700'))

// The title and objective lead whichever page prints first: the passages page when there is
// one, otherwise the item page, which then prints even with the prompt and response unticked.
const isPassagesPagePrinted = computed(() => hasPassages.value && printOptions.includePassages)
const isItemPagePrinted = computed(
  () => printOptions.includeItem || printOptions.includeResponse || !isPassagesPagePrinted.value
)
const isObjectivePrinted = computed(() => printOptions.includeObjective && Boolean(props.objectiveText))

const standardRefLabel = computed(() => {
  const standardRef = item.value?.standard_ref
  if (teksVisibility.value || !standardRef) {
    return ''
  }
  return `(${shortCcssRef(standardRef)})`
})

const introProps = computed(() => ({
  title: props.objectiveTitle,
  standardRefLabel: standardRefLabel.value,
  objectiveText: props.objectiveText,
  isObjectivePrinted: isObjectivePrinted.value
}))

function optionId(option: keyof ObjectivePrintOptions) {
  return `${optionIdPrefix}-${option}`
}


function close() {
  emit('close')
}

function printItem() {
  printAndClose(() => emit('print', { has_passages: hasPassages.value, ...printOptions }))
}

async function loadItem() {
  try {
    await itemsStore.fetchItem(props.itemId)
    if (!hasPassages.value) {
      printOptions.includePassages = false
    }
  } catch (error) {
    console.error(error)
    errorMessage.value = FETCH_ERROR_MESSAGE
  } finally {
    isFetchingItem.value = false
  }
}

loadItem()
</script>

<template>
  <PrintPreviewOverlay :is-initializing="isPreparing" label="Print preview" @close="close">
    <PrintPreparing v-if="isPreparing" />

    <div v-else-if="errorMessage" class="flex flex-col items-center gap-4 p-8 text-center">
      <p role="alert" class="text-text-lg text-red-300">{{ errorMessage }}</p>
      <button type="button" :class="DEFAULT_BUTTON_CLASSES" @click="close">Close</button>
    </div>

    <PrintLayoutContainer v-else-if="item">
      <template #top>
        <div class="container flex flex-wrap gap-3">
          <fieldset class="flex-auto">
            <legend class="font-bold">Include:</legend>
            <div class="flex items-center gap-2">
              <Checkbox v-model="printOptions.includeObjective" binary :input-id="optionId('includeObjective')" />
              <label :for="optionId('includeObjective')">Objective</label>
            </div>
            <div v-if="hasPassages" class="flex items-center gap-2">
              <Checkbox v-model="printOptions.includePassages" binary :input-id="optionId('includePassages')" />
              <label :for="optionId('includePassages')">Passages</label>
            </div>
            <div class="flex items-center gap-2">
              <Checkbox v-model="printOptions.includeItem" binary :input-id="optionId('includeItem')" />
              <label :for="optionId('includeItem')">Item Prompt</label>
            </div>
            <div class="flex items-center gap-2">
              <Checkbox v-model="printOptions.includeResponse" binary :input-id="optionId('includeResponse')" />
              <label :for="optionId('includeResponse')">Model Student Response</label>
            </div>
          </fieldset>
          <p v-if="isTrial" class="flex-auto pt-1.5 text-right" :class="printQuotaClass" data-testid="printable-quota">
            {{ printsRemaining }} of {{ printQuota }} prints remaining this month
          </p>
          <div class="flex flex-none flex-col gap-2 pl-3">
            <button type="button" class="w-full justify-center" :class="printButtonClasses" data-testid="printable-print" @click="printItem">
              <span class="material-icons-round text-text-lg" aria-hidden="true">print</span>
              Print
            </button>
            <button type="button" class="w-full justify-center" :class="DEFAULT_BUTTON_CLASSES" data-testid="printable-cancel" @click="close">
              <span class="material-icons-round text-text-lg" aria-hidden="true">close</span>
              Cancel
            </button>
          </div>
        </div>
      </template>

      <template #body>
        <PrintPage v-if="isPassagesPagePrinted" data-testid="printable-passages-page">
          <PrintableObjectiveIntro v-bind="introProps" />
          <PrintSectionHeading>Passages</PrintSectionHeading>
          <section v-for="(passage, index) in item.passages" :key="passage.uuid ?? index">
            <h3 class="mb-2.5 mt-5 text-display-sm font-medium">{{ passage.passage_title }}</h3>
            <MarkdownText :text="passage.passage_text" class="[font-family:Times,serif]" />
          </section>
        </PrintPage>

        <PrintPage v-if="isItemPagePrinted" data-testid="printable-item-page">
          <PrintableObjectiveIntro v-if="!isPassagesPagePrinted" v-bind="introProps" />

          <section>
            <div v-if="printOptions.includeItem" data-testid="printable-prompts">
              <PrintSectionHeading>Item Prompt</PrintSectionHeading>
              <div v-for="(part, index) in item.parts" :key="index">
                <h4 v-if="hasMultipleParts" class="mb-2.5 mt-2.5 text-text-xl font-medium">{{ partHeading(index) }}</h4>
                <WikiText :text="part.prompt" />
              </div>
            </div>

            <div v-if="printOptions.includeResponse" data-testid="printable-responses">
              <PrintSectionHeading>Model Student Response</PrintSectionHeading>
              <div v-for="(part, index) in item.parts" :key="index">
                <h4 v-if="hasMultipleParts" class="mb-2.5 mt-2.5 text-text-xl font-medium">{{ partHeading(index) }}</h4>
                <WikiText :text="part.response" />
              </div>
            </div>
          </section>
        </PrintPage>
      </template>
    </PrintLayoutContainer>
  </PrintPreviewOverlay>
</template>
